import React, { useState, useEffect } from "react";
import { Box, TextField } from "@mui/material";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

interface FilterComponentProps {
  columns: string[];
  columnPlaceholders: string[];
  onFilter: (filters: { [key: string]: string }) => void;
}

const DATE_COLUMNS = ["firstDate", "lastDate"];

const FilterComponent: React.FC<FilterComponentProps> = ({
  columns,
  columnPlaceholders,
  onFilter,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [dateFilters, setDateFilters] = useState<{
    [key: string]: { gte: string; lte: string };
  }>({});

  const parseDateFilterString = (val: string): { gte: string; lte: string } => {
    const parts = val.split("&");
    const parsed: { gte: string; lte: string } = { gte: "", lte: "" };
    parts.forEach((part) => {
      if (part.startsWith("gte:")) parsed.gte = part.slice(4);
      else if (part.startsWith("lte:")) parsed.lte = part.slice(4);
    });
    return parsed;
  };

  useEffect(() => {
    const newFilters: { [key: string]: string } = {};
    const newDateFilters: { [key: string]: { gte: string; lte: string } } = {};

    columns.forEach((column) => {
      const val = searchParams.get(column);
      if (val) {
        if (DATE_COLUMNS.includes(column)) {
          newDateFilters[column] = parseDateFilterString(val);
        } else {
          newFilters[column] = val;
        }
      }
    });

    setFilters(newFilters);
    setDateFilters(newDateFilters);

    onFilter({
      ...newFilters,
      ...Object.fromEntries(
        Object.entries(newDateFilters).map(([k, { gte, lte }]) => {
          let val = "";
          if (gte) val += `gte:${gte}`;
          if (lte) val += (gte ? "&" : "") + `lte:${lte}`;
          return [k, val];
        }),
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [field]: value || "" }));
  };

  const handleDateChange = (
    newValue: any,
    column: string,
    type: "gte" | "lte",
  ) => {
    const formattedDate = newValue ? dayjs(newValue).format("DD-MM-YYYY") : "";
    setDateFilters((prev) => ({
      ...prev,
      [column]: {
        ...prev[column],
        [type]: formattedDate,
      },
    }));
  };

  const handleSearch = () => {
    const combinedFilters = { ...filters };

    Object.entries(dateFilters).forEach(([dateField, { gte, lte }]) => {
      if (gte || lte) {
        let val = "";
        if (gte) val += `gte:${gte}`;
        if (lte) val += (gte ? "&" : "") + `lte:${lte}`;
        combinedFilters[dateField] = val;
      } else {
        delete combinedFilters[dateField];
      }
    });

    Object.keys(combinedFilters).forEach((key) => {
      if (!combinedFilters[key]) {
        delete combinedFilters[key];
      }
    });

    const params = new URLSearchParams();
    Object.entries(combinedFilters).forEach(([key, val]) => {
      params.set(key, val);
    });
    setSearchParams(params);

    onFilter(combinedFilters);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box
      sx={{
        background: grey[100],
        borderRadius: "4px",
        padding: "1.4rem",
        paddingTop: "0.6rem",
        mt: "1rem",
        maxWidth: "100%",
      }}
    >
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        flexWrap="wrap"
      >
        {columns.map((column, index) => (
          <Box key={column} sx={{ mt: "0.4rem", mr: "1rem" }}>
            <Typography variant="subtitle1">
              {columnPlaceholders[index]}
            </Typography>
            {DATE_COLUMNS.includes(column) ? (
              <Box
                display={"flex"}
                flexDirection={"row"}
                flexWrap={"wrap"}
                onKeyDown={handleKeyPress}
              >
                <DatePicker
                  label={"From"}
                  sx={{ background: "white", mr: "0.4rem" }}
                  slotProps={{
                    textField: { size: "small" },
                    clearButton: { size: "small" },
                  }}
                  value={
                    dateFilters[column]?.gte
                      ? dayjs(dateFilters[column]?.gte, "DD-MM-YYYY")
                      : null
                  }
                  onChange={(newValue) => {
                    handleDateChange(newValue, column, "gte");
                    handleSearch();
                  }}
                />
                <DatePicker
                  label={"To"}
                  sx={{ background: "white" }}
                  slotProps={{ textField: { size: "small" } }}
                  value={
                    dateFilters[column]?.lte
                      ? dayjs(dateFilters[column]?.lte, "DD-MM-YYYY")
                      : null
                  }
                  onChange={(newValue) => {
                    handleDateChange(newValue, column, "lte");
                    handleSearch();
                  }}
                />
              </Box>
            ) : (
              <TextField
                size="small"
                variant="outlined"
                sx={{ background: "white" }}
                value={filters[column] || ""}
                onChange={(e) => {
                  handleInputChange(column, e.target.value);
                }}
                onKeyDown={handleKeyPress}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FilterComponent;
